import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function ContactSection() {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  const contacts = [
    { key: 'contact.email', value: 'fabriaguirre@gmail.com', icon: '📧', href: 'mailto:fabriaguirre@gmail.com' },
    { key: 'contact.github', value: 'GitHub', icon: '💻', href: 'https://github.com/MascotaCool/principal' },
    { key: 'contact.linkedin', value: 'LinkedIn', icon: '💼', href: 'https://www.linkedin.com/in/fabricio-aguirre-1979may28' },
    { key: 'contact.whatsapp', value: 'WhatsApp', icon: '💬', href: 'https://api.whatsapp.com/send?phone=593998741295&text=Hola,%21%20Ayudame%20like%20more%20information%20about%20the%20projects.%20Thank%20you' },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      const contactCards = containerRef.current?.querySelectorAll('.contact-card');
      if (contactCards) {
        gsap.fromTo(
          contactCards,
          { opacity: 0, y: 40, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 75%',
            },
          }
        );
      }

      const closingTexts = containerRef.current?.querySelectorAll('.closing-text');
      if (closingTexts) {
        gsap.fromTo(
          closingTexts,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 60%',
            },
          }
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={containerRef}
      id="contact"
      className="contact-section"
      style={{
        minHeight: '100vh',
        padding: '6rem 2rem',
        position: 'relative',
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
      }}
      aria-labelledby="contact-title"
    >
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <header className="section-header" style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 id="contact-title" className="section-title" style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, var(--fg-primary), var(--accent-primary))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            {t('contact.title')}
          </h2>
          <p className="section-subtitle" style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: 'var(--fg-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            {t('contact.subtitle')}
          </p>
        </header>

        <div className="contact-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '4rem',
          maxWidth: '800px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          {contacts.map((contact) => (
            <a
              key={contact.key}
              href={contact.href}
              target="_blank"
              rel="noopener noreferrer"
              className="contact-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
                padding: '2rem',
                background: 'var(--card-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                color: 'inherit',
              }}
              onMouseEnter={e => {
                gsap.to(e.currentTarget, {
                  borderColor: 'var(--accent-primary)',
                  boxShadow: '0 20px 40px rgba(0, 255, 136, 0.15)',
                  transform: 'translateY(-4px)',
                  duration: 0.3,
                });
              }}
              onMouseLeave={e => {
                gsap.to(e.currentTarget, {
                  borderColor: 'var(--border-color)',
                  boxShadow: 'none',
                  transform: 'translateY(0)',
                  duration: 0.3,
                });
              }}
            >
              <span
                className="contact-icon"
                style={{
                  fontSize: '1.8rem',
                  width: '58px',
                  height: '58px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, var(--accent-primary)20, var(--accent-secondary)05)',
                  border: '1px solid var(--accent-primary)40',
                  borderRadius: '14px',
                }}
              >
                {contact.icon}
              </span>
              <div style={{
                minWidth: 0,
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
                <span className="contact-label" style={{
                  display: 'block',
                  fontSize: '0.7rem',
                  color: 'var(--fg-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  marginBottom: '0.35rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {t(contact.key)}
                </span>
                <span className="contact-value" style={{
                  display: 'block',
                  fontSize: contact.value.includes('@') ? '0.95rem' : '1.125rem',
                  fontWeight: 500,
                  color: 'var(--fg-primary)',
                  lineHeight: 1.4,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  fontFamily: contact.value.includes('@') ? "'JetBrains Mono', monospace" : 'inherit',
                }}>
                  {contact.value}
                </span>
              </div>
            </a>
          ))}
        </div>

        <div className="closing-animation" style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          borderTop: '1px solid var(--border-color)',
        }}>
          <p className="closing-text" style={{
            fontSize: 'clamp(1.25rem, 3vw, 2rem)',
            fontWeight: 300,
            color: 'var(--fg-secondary)',
            marginBottom: '1rem',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {t('contact.message')}
          </p>
          <div className="closing-line" style={{
            width: '100px',
            height: '2px',
            background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
            margin: '0 auto 2rem',
            borderRadius: '1px',
          }} />
          <p className="closing-text" style={{
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            color: 'var(--accent-primary)',
            fontWeight: 500,
          }}>
            JFAguirreT2027
          </p>
        </div>
      </div>
    </section>
  );
}